import { createSlice, createEntityAdapter, EntityState, createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { createCharge, fetchCharges, addAssetLinkages, addLoanLinkages } from "./chargesThunk";
import { Charge, AssetLinkage, LoanLinkage, ChargeWithLinkages, SECURITY_ROLE_LABELS, CHARGE_MODE_LABELS, DISTRIBUTION_RIGHTS_LABELS, PUBLIC_CHARGE_REGISTRY_LABELS } from "../../../api/charges";

import { Asset, ASSET_TYPE_LABELS, AssetType } from "../../../api/assets";
import { RequestStatus, selectAssetEntities } from "./assetsSlice";
import { getAccountProductInfo } from "../../creditAccounts/logic/slice";

export const chargesAdapter = createEntityAdapter<Charge, string>({
  selectId: (charge) => charge.charge_id,
});

export const assetLinkagesAdapter = createEntityAdapter<AssetLinkage, string>({
  selectId: (linkage) => linkage.linkage_id,
});

export const loanLinkagesAdapter = createEntityAdapter<LoanLinkage, string>({
  selectId: (linkage) => linkage.linkage_id,
});

export interface ChargesState {
  charges: EntityState<Charge, string>;
  asset_linkages: EntityState<AssetLinkage, string>;
  loan_linkages: EntityState<LoanLinkage, string>;
  assetLinkagesByChargeId: Record<string, string[]>;
  loanLinkageByChargeId: Record<string, string[]>;
  chargeIdsByLoanReferenceId: Record<string, string[]>;
  caseId?: string;
  status: RequestStatus;
  error?: string | null;
}

const initialState: ChargesState = {
  charges: chargesAdapter.getInitialState(),
  asset_linkages: assetLinkagesAdapter.getInitialState(),
  loan_linkages: loanLinkagesAdapter.getInitialState(),
  assetLinkagesByChargeId: {},
  loanLinkageByChargeId: {},
  chargeIdsByLoanReferenceId: {},
  caseId: undefined,
  status: "idle",
  error: null,
};

const appendAssetLinkagesToMap = (
  state: ChargesState,
  linkages: AssetLinkage[]
) => {
  linkages.forEach((linkage) => {
    const { charge_id, linkage_id } = linkage;
    if (!state.assetLinkagesByChargeId[charge_id]) {
      state.assetLinkagesByChargeId[charge_id] = [];
    }
    if (!state.assetLinkagesByChargeId[charge_id].includes(linkage_id)) {
      state.assetLinkagesByChargeId[charge_id].push(linkage_id);
    }
  });
};

const appendLoanLinkagesToMaps = (
  state: ChargesState,
  linkages: LoanLinkage[]
) => {
  linkages.forEach((linkage) => {
    const { charge_id, loan_reference_id, linkage_id } = linkage;

    if (!state.loanLinkageByChargeId[charge_id]) {
      state.loanLinkageByChargeId[charge_id] = [];
    }
    if (!state.loanLinkageByChargeId[charge_id].includes(linkage_id)) {
      state.loanLinkageByChargeId[charge_id].push(linkage_id);
    }

    if (!state.chargeIdsByLoanReferenceId[loan_reference_id]) {
      state.chargeIdsByLoanReferenceId[loan_reference_id] = [];
    }
    if (!state.chargeIdsByLoanReferenceId[loan_reference_id].includes(charge_id)) {
      state.chargeIdsByLoanReferenceId[loan_reference_id].push(charge_id);
    }
  });
};

const chargesSlice = createSlice({
  name: "charges",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCharges.pending, (state) => {
        state.status = "loading";
        state.error = null;
        chargesAdapter.removeAll(state.charges);
        assetLinkagesAdapter.removeAll(state.asset_linkages);
        loanLinkagesAdapter.removeAll(state.loan_linkages);
        state.assetLinkagesByChargeId = {};
        state.loanLinkageByChargeId = {};
        state.chargeIdsByLoanReferenceId = {};
      })

      .addCase(fetchCharges.fulfilled, (state, action) => {
        state.status = "success";
        state.error = null;
        state.caseId = action.meta.arg;
        
        chargesAdapter.setAll(state.charges, action.payload.charges);
        assetLinkagesAdapter.setAll(state.asset_linkages, action.payload.asset_linkages);
        loanLinkagesAdapter.setAll(state.loan_linkages, action.payload.loan_linkages);

        appendAssetLinkagesToMap(state, action.payload.asset_linkages);
        appendLoanLinkagesToMaps(state, action.payload.loan_linkages);
      })

      .addCase(fetchCharges.rejected, (state, action) => {
        state.status = "failed";
        state.error = "Failed to fetch charges";
      })

      .addCase(createCharge.fulfilled, (state, action) => {
        chargesAdapter.addOne(state.charges, action.payload.charges);
        assetLinkagesAdapter.addMany(state.asset_linkages, action.payload.asset_linkages);
        loanLinkagesAdapter.addMany(state.loan_linkages, action.payload.loan_linkages);

        appendAssetLinkagesToMap(state, action.payload.asset_linkages);
        appendLoanLinkagesToMaps(state, action.payload.loan_linkages);
      })

      .addCase(addAssetLinkages.fulfilled, (state, action) => {
        assetLinkagesAdapter.addMany(state.asset_linkages, action.payload.asset_linkages);
        appendAssetLinkagesToMap(state, action.payload.asset_linkages);
      })

      .addCase(addLoanLinkages.fulfilled, (state, action) => {
        loanLinkagesAdapter.addMany(state.loan_linkages, action.payload.loan_linkages);
        appendLoanLinkagesToMaps(state, action.payload.loan_linkages);
      });
  },
});

export const chargesReducer = chargesSlice.reducer;

const ChargesSelectors = chargesAdapter.getSelectors<RootState>(
  (state) => state.charges.charges
);
const AssetLinkagesSelectors = assetLinkagesAdapter.getSelectors<RootState>(
  (state) => state.charges.asset_linkages
);
const LoanLinkagesSelectors = loanLinkagesAdapter.getSelectors<RootState>(
  (state) => state.charges.loan_linkages
);

export const selectChargesStatus = (state: RootState) => state.charges.status;

export const selectChargesError = (state: RootState) => state.charges.error;

export const selectChargeIds = (state: RootState) => state.charges.charges.ids;

export const selectChargeCount = (state: RootState) => state.charges.charges.ids.length;

export const selectChargeEntities = createSelector(
  [
    ChargesSelectors.selectEntities,
    AssetLinkagesSelectors.selectEntities,
    LoanLinkagesSelectors.selectEntities,
    (state: RootState) => state.charges.assetLinkagesByChargeId,
    (state: RootState) => state.charges.loanLinkageByChargeId,
  ],
  (chargeEntities, assetEntities, loanEntities, assetMap, loanMap) => {
    const Charges: Record<string, ChargeWithLinkages> = {};

    Object.keys(chargeEntities).forEach((chargeId) => {
      const charge = chargeEntities[chargeId];
      if (!charge) return;

      const assetLinkageIds = assetMap[chargeId] || [];
      const loanLinkageIds = loanMap[chargeId] || [];

      const assetLinkages = assetLinkageIds
        .map((id) => assetEntities[id])

      const loanLinkages = loanLinkageIds
        .map((id) => loanEntities[id])

      Charges[chargeId] = {
        charges: charge,
        asset_linkages: assetLinkages,
        loan_linkages: loanLinkages,
      };
    });

    return Charges;
  }
);

export const selectAllCharges = createSelector(
  [selectChargeIds, selectChargeEntities],
  (ids, entities) => {
    return ids
      .map((id) => entities[id])
  }
);

export const selectChargeById = createSelector(
  [
    selectChargeEntities,
    (_state: RootState, chargeId?: string) => chargeId,
  ],
  (entities, chargeId) => {
    if (!chargeId) return undefined;
    return entities[chargeId];
  }
);

export const selectAssetIdsForCharge = createSelector(
  [
    selectChargeEntities,
    (_state: RootState, chargeId?: string) => chargeId
  ],
  (entities, chargeId) => {
    if (!chargeId) {
      return [];
    }
    const linkages = entities[chargeId]?.asset_linkages ?? [];
    return linkages.map((linkage) => linkage.asset_id);
  }
);

export const selectLoanReferenceIdsForCharge = createSelector(
  [
    selectChargeEntities,
    (_state: RootState, chargeId?: string) => chargeId
  ],
  (entities, chargeId) => {
    if (!chargeId) {
      return [];
    }
    const linkages = entities[chargeId]?.loan_linkages ?? [];
    return linkages.map((linkage) => linkage.loan_reference_id);
  }
);

export const selectChargesByLoanReferenceId = createSelector(
  [
    ChargesSelectors.selectEntities,
    (state: RootState) => state.charges.chargeIdsByLoanReferenceId,
    (_state: RootState, loanReferenceId?: string) => loanReferenceId
  ],
  (chargeEntities, chargeIdsMap, loanReferenceId) => {
    if (!loanReferenceId) return [];
    const chargeIds = chargeIdsMap[loanReferenceId] || [];
    return chargeIds
      .map((id) => chargeEntities[id])
      .filter((charge): charge is Charge => !!charge);
  }
);

export const selectCreditAccountsForCharge = createSelector(
  [
    selectChargeEntities,
    getAccountProductInfo,
    (_: RootState, chargeId?: string) => chargeId
  ],
  (entities, accountProductInfo, chargeId) => {
    if (!chargeId) return [];

    const linkages = entities[chargeId]?.loan_linkages ?? [];

    return accountProductInfo.filter((account) =>
      linkages.some((linkage) => linkage.loan_reference_id === account.loan_reference_id),
    );
  }
);

export const selectAssetsForCharge = createSelector(
  [
    selectChargeEntities,
    selectAssetEntities,
    (_state: RootState, chargeId?: string) => chargeId
  ],
  (chargeEntities, assetEntities, chargeId) => {
    const charge = chargeId ? chargeEntities[chargeId] : undefined;
    if (!charge) return [];

    return charge.asset_linkages
      .map((linkage) => assetEntities[linkage.asset_id])
      .filter((asset): asset is Asset => !!asset);
  }
);

export const selectAssetsByChargeIds = createSelector(
  [
    selectChargeEntities,
    selectAssetEntities,
    (_state: RootState, chargeIds: string[]) => chargeIds
  ],
  (chargeEntities, assetEntities, chargeIds) => {
    const assetsMap: Record<string, Asset[]> = {};
    if (!chargeIds) return assetsMap;

    chargeIds.forEach((chargeId) => {
      const charge = chargeEntities[chargeId];
      if (!charge) {
        assetsMap[chargeId] = [];
        return;
      }
      assetsMap[chargeId] = charge.asset_linkages
        .map((linkage) => assetEntities[linkage.asset_id])
        .filter((asset): asset is Asset => !!asset);
    });

    return assetsMap;
  }
);

export const selectChargeCardsForLoanReference = createSelector(
  [
    selectAllCharges,
    selectAssetEntities,
    (_state: RootState, loanReferenceId?: string) => loanReferenceId
  ],
  (charges, assetEntities, loanReferenceId) => {

    if (!loanReferenceId) return [];

    return charges
      .filter((chargeResponse) =>
        chargeResponse.loan_linkages.some(
          (linkage) => linkage.loan_reference_id === loanReferenceId,
        )
      )
      .map((chargeResponse) => {
        const assetTypes: AssetType[] = [];

        chargeResponse.asset_linkages.forEach((linkage) => {
          const assetType = assetEntities[linkage.asset_id]?.asset_type;

          if (assetType && !assetTypes.includes(assetType)) {
            assetTypes.push(assetType);
          }
        });

        const securityInterestId = chargeResponse.charges.registry_identifiers.security_interest_id;

        return {
          id: chargeResponse.charges.charge_id,
          name: `${SECURITY_ROLE_LABELS[chargeResponse.charges.security_role!]}  (${securityInterestId})`,
          description: `${CHARGE_MODE_LABELS[chargeResponse.charges.charge_mode!]} • ${DISTRIBUTION_RIGHTS_LABELS[chargeResponse.charges.distribution_rights!]} • ${PUBLIC_CHARGE_REGISTRY_LABELS[chargeResponse.charges.public_registry]}`,
          assetTypes: assetTypes.map(type => ASSET_TYPE_LABELS[type]).join(", "),
        };
      });
  }
);